import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Button,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import expenseService from '../services/expenseService';
import DataTable from '../components/DataTable';

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name_en: '',
    description: ''
  });

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await expenseService.getExpenses(page, limit);
      setExpenses(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching expenses');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleAddClick = () => {
    setSelectedExpense(null);
    setFormData({
      name_en: '',
      description: ''
    });
    setDialogOpen(true);
  };

  const handleEditClick = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      name_en: expense.name_en,
      description: expense.description || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExpenseSearch = (query = null) => {
    const searchTerm = query !== null ? query : searchQuery;

    if (!searchTerm.trim()) {
      fetchExpenses();
      return;
    }

    // Filter expenses based on name
    const filtered = expenses.filter(expense =>
      expense.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setExpenses(filtered);
  };

  const handleExpenseSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Trigger search as user types (real-time)
    if (query.trim() || query === '') {
      handleExpenseSearch(query);
    }
  };

  const handleSave = async () => {
    if (!formData.name_en) {
      setError('Please fill in the expense name');
      return;
    }

    try {
      if (selectedExpense) {
        await expenseService.updateExpense(selectedExpense._id, formData);
      } else {
        await expenseService.createExpense(formData);
      }
      setDialogOpen(false);
      setError('');
      setSuccess(selectedExpense ? 'Expense updated successfully' : 'Expense created successfully');
      fetchExpenses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving expense');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await expenseService.deleteExpense(deleteId);
      setDeleteConfirmOpen(false);
      setError('');
      setSuccess('Expense deleted successfully');
      fetchExpenses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting expense');
    }
  };

  const columns = [
    {
      id: 'name_en',
      label: 'Expense Name (English)'
    },
    {
      id: 'description',
      label: 'Description'
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (row) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEditClick(row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(row._id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <TextField
          placeholder="Search expenses..."
          value={searchQuery}
          onChange={handleExpenseSearchInputChange}
          size="small"
          fullWidth
          variant="outlined"
        />
        <Button variant="contained" color="success" onClick={handleAddClick}>
          + Add Expense
        </Button>
      </Box>

      <DataTable
        columns={columns}
        rows={expenses}
        loading={loading}
        pagination={{ page, limit, total }}
        onPageChange={setPage}
        onRowsPerPageChange={setLimit}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedExpense ? 'Edit Expense' : 'Add Expense'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Expense Name (English)"
            name="name_en"
            value={formData.name_en}
            onChange={handleFormChange}
            fullWidth
            required
          />
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this expense?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ExpensesPage;
